<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useDefaultKeyHandler as useDefaultGridKeyHandler } from '@/composables/data-grid'
import { usePortalStore } from '@/stores/portal.store.js'
import { useWebSiteStore } from '@/stores/web-site.store.js'
import { useChatStore } from '@/stores/chat.store'
import { useOrientation, useDetectDevice } from '@/composables/device'

import AvatarEdit from '@/components/shared/AvatarEdit.vue'
import BreadCrumb from '@/components/shared/BreadCrumb.vue'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'
import SideBar from '@/components/shared/SideBar.vue'
import UniversalSearch from '@/components/shared/UniversalSearch.vue'
import FontSizeSelector from '@/components/shared/FontSizeSelector.vue'
import NotificationIcon from '@/components/shared/NotificationIcon.vue'
import TasksButton from '@/components/task/TasksButton.vue'
import TasksPanel from '@/components/task/TasksPanel.vue'
import NotificationsPanel from '@/components/NotificationsPanel.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'
import ChatPanel from '@/components/chat/ChatPanel.vue'
import SearchResultDetail from '@/components/shared/SearchResultDetail.vue'

useDefaultGridKeyHandler()

const route = useRoute()
const signalBarPage = computed(() => route.meta.signalBar)

const FAB_OFFSET = Object.freeze([18, 18])
const FAB_OFFSET_ABOVE_BOTTOM_TABS = Object.freeze([18, 58])
const fabOffset = computed(() => (route.meta.bottomTabs ? FAB_OFFSET_ABOVE_BOTTOM_TABS : FAB_OFFSET))

const { isLandscape } = useOrientation()
const { isMobile } = useDetectDevice()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const chatStore = useChatStore()

const drawer = ref(!isMobile.value)

const userName = computed(() => {
  if (webSiteStore.user && webSiteStore.user.firstName) {
    return `${webSiteStore.user.firstName} ${webSiteStore.user.lastName}`
  }
  return webSiteStore.user?.username ?? ''
})

const onLogoutAsync = async () => await webSiteStore.logoutAsync()

const sidePanels = [
  { isShown: () => webSiteStore.showTasks, isDocked: () => webSiteStore.taskPanelDocked, hide: () => { webSiteStore.showTasks = false } },
  { isShown: () => chatStore.showChat, isDocked: () => webSiteStore.chatPanelDocked, hide: () => { chatStore.showChat = false } },
  { isShown: () => webSiteStore.showNotifications, isDocked: () => webSiteStore.notificationPanelDocked, hide: () => { webSiteStore.showNotifications = false } },
]
// Docked panels are the right drawer of the layout (on mobile, the same bottom sheet): only one fits.
const occupiesSharedSpace = (panel) => isMobile.value || panel.isDocked()
sidePanels.forEach(panel => watch(() => [panel.isShown(), panel.isDocked()], ([show]) => {
  if (!show || !occupiesSharedSpace(panel)) return
  sidePanels.filter(other => other !== panel && other.isShown() && occupiesSharedSpace(other)).forEach(other => other.hide())
}))


</script>
<template>
  <q-layout view="hHh LpR lFf">
    <q-header
      :model-value="!webSiteStore.topBarHidden"
      elevated
      class="header"
    >
      <q-toolbar
        :class="['text-white', 'toolbar', isMobile ? 'mobile-toolbar' : '']"
        elevated
      >
        <q-img
          src="/img/logo-white.svg"
          height="2rem"
          width="2rem"
          min-width="2rem"
          class="q-mr-sm logo-button"
          aria-label="Menu"
          style="cursor: pointer;"
          @click="drawer = !drawer"
        />
        <div class="title">
          <span
            v-if="isLandscape"
            class="text-h6"
          >
            Reel-Sync
          </span>
          <bread-crumb
            v-if="isMobile"
            class="q-ml-md"
            color="white"
            @open-nav="drawer = true"
          />
        </div>
        <q-space />
        <language-selector
          v-if="isLandscape"
          inverted
        />
        <q-btn
          round
          flat
          icon="mdi-magnify"
          :aria-label="$t('common.search')"
          @click="webSiteStore.showSearch = true"
        >
          <q-tooltip>{{ $t('common.search') }}</q-tooltip>
        </q-btn>
        <filter-sort-selector icon />
        <tasks-button />
        <notification-icon />
        <q-btn
          v-if="chatStore.isEnabled"
          round
          flat
          dense
          icon="mdi-creation"
          :aria-label="$t('chat.title')"
          @click="chatStore.showChat = !chatStore.showChat"
        >
          <q-tooltip>{{ $t('chat.title') }}</q-tooltip>
        </q-btn>
        <q-separator
          v-if="isLandscape"
          dark
          vertical
          inset
          class="q-mx-md"
        />
        <q-btn
          v-if="isLandscape"
          round
          flat
          icon="mdi-dots-vertical"
        >
          <q-menu
            anchor="bottom right"
            self="top right"
            class="menu-list"
          >
            <div class="row no-wrap q-pa-md">
              <div style="display: flex; flex-direction: column; align-items: center;">
                <div class="text-h6 q-mb-md">
                  {{ $t('common.setting', 2) }}
                </div>
                <font-size-selector />
              </div>
              <q-separator
                vertical
                inset
                class="q-mx-lg"
              />


              <div style="display: flex; flex-direction: column; align-items: center; flex-grow: 1;">
                <avatar-edit
                  v-close-popup
                  size="4.5rem"
                  :label="$t('common.myProfile')"
                  @click="webSiteStore.navigateToMyProfile()"
                />
                <div class="text-subtitle1 q-mt-md q-mb-xs">
                  {{ userName }}
                </div>

                <q-btn
                  v-close-popup
                  color="primary"
                  :label="$t('common.logout')"
                  size="sm"
                  @click="onLogoutAsync"
                />
                <q-badge
                  style="background: transparent; color: grey;"
                  :label="`app v.${portalStore.version}`"
                  class="q-mt-sm"
                />
              </div>
            </div>
          </q-menu>
        </q-btn>
      </q-toolbar>
    </q-header>
    <SideBar v-model:drawer="drawer" />
    <chat-panel v-if="chatStore.isEnabled" />
    <tasks-panel />
    <notifications-panel />
    <universal-search v-model="webSiteStore.showSearch" />
    <search-result-detail />
    <q-page-container>
      <q-page :padding="!isMobile">
        <div v-if="!isMobile" class="row items-center no-wrap page-header">
          <bread-crumb color="primary" />
          <q-space />
          <q-btn-group v-if="webSiteStore.pageTabOptions.length" flat>
            <q-btn
              v-for="opt in webSiteStore.pageTabOptions"
              :key="opt.value"
              flat
              no-caps
              :label="opt.label"
              :icon="opt.icon"
              :color="webSiteStore.currentPageTab === opt.value ? 'primary' : 'grey-6'"
              @click="webSiteStore.onPageTabChange?.(opt.value)"
            />
          </q-btn-group>
          <signal-bar v-if="signalBarPage" :page="signalBarPage" class="q-ml-md" />
        </div>
        <router-view v-slot="{ Component }">
          <transition
            appear
            name="page"
            mode="out-in"
          >
            <component
              :is="Component"
              :key="portalStore.reloadKey"
            />
          </transition>
        </router-view>
      </q-page>
      <q-page-sticky
        position="bottom-right"
        :offset="fabOffset"
      >
        <q-fab
          v-if="isMobile && webSiteStore.fabActions.length"
          fab
          icon="mdi-chevron-up"
          color="primary"
          direction="up"
        >
          <q-fab-action
            v-for="fabAction in webSiteStore.fabActions"
            :key="fabAction.id"
            :color="fabAction.color || 'accent'"
            :icon="fabAction.icon"
            :label="fabAction.label"
            label-position="left"
            @click="fabAction.onClick"
          />
        </q-fab>
      </q-page-sticky>
    </q-page-container>
  </q-layout>
</template>

<style scoped lang="scss">
.header {
  z-index: 1000;
  padding-top: env(safe-area-inset-top);
  padding-top: constant(safe-area-inset-top);
}

q-fab {
  z-index: 10000;
}

.menu-list {
  min-width: 23rem;
}


.toolbar {
  background: linear-gradient(90deg, mix(white, $primary, 20%), $secondary);
}

.mobile-toolbar {
  min-height: 4rem;
}

.logo-button {
  flex: 0 0 auto;
}

.title {
  display: flex;
  align-items: baseline;
  min-width: 0;
  overflow: hidden;
}

.breadcrumb {
  font-size: small;
  display: flex;
  align-items: baseline;
  z-index: 2;
}
</style>
